// Quick add on collection cards (snippets/custom-product-card-quick-add.liquid) and the homepage product rows.
// Adds to the cart without leaving the page, flies the product image into the cart icon and bumps the count.
// Products with several variants open a small picker first.
// Any button with data-quick-add-variant (or data-quick-add-options) inside a data-quick-add-card or
// .custom-product-card works; the card's first image is the one that flies.
(() => {
  const ADDED_DURATION = 2000;
  const ERROR_DURATION = 6000;
  const GENERIC_ERROR = "Couldn't add this item. Please try again.";
  const BUTTON_ICONS = `
    <span class="card-quick-add__icon" aria-hidden="true">
      <svg class="card-quick-add__plus" viewBox="0 0 16 16" fill="none">
        <path d="M8 2.5v11M2.5 8h11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <svg class="card-quick-add__check" viewBox="0 0 16 16" fill="none">
        <path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </span>`;

  let modal = null;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('button[data-quick-add-variant], button[data-quick-add-options]');
    if (!button || button.disabled || button.classList.contains('is-loading')) return;

    event.preventDefault();
    const card = button.closest('[data-quick-add-card], .custom-product-card');

    if (button.dataset.quickAddVariant) {
      addFromCard(button, card);
    } else if (button.hasAttribute('data-quick-add-options')) {
      openPicker(button, card);
    }
  });

  async function addFromCard(button, card) {
    const error = button.parentElement.querySelector('.card-quick-add__error');
    hideError(error);
    setLoading(button, true);

    try {
      const data = await requestAdd(button.dataset.quickAddVariant);
      setLoading(button, false);
      showAdded(button);
      celebrate(card, data.sections?.['custom-header']);
      trackAdd(data.items?.[0], button.closest('.card-quick-add') ? 'Collection quick add' : 'Homepage quick add');
    } catch (err) {
      setLoading(button, false);
      showError(error, err.message);
    }
  }

  async function requestAdd(variantId) {
    let response;
    let data = {};

    try {
      response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: 'custom-header' }),
      });
      data = await response.json();
    } catch (error) {
      throw new Error(GENERIC_ERROR);
    }

    if (!response.ok) throw new Error(data.description || GENERIC_ERROR);
    return data;
  }

  // Feedback: reveal the hidden sticky header, fly the card image into the cart icon, then bump the new count.
  async function celebrate(card, headerHtml) {
    const cartIcon = document.getElementById('cart-icon-bubble');
    const image = card?.querySelector('img');

    if (cartIcon && image && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (revealHeader()) await wait(200);
      await flyToCart(image, cartIcon);
    }

    updateCartBubble(headerHtml);
  }

  function revealHeader() {
    const header = document.querySelector('.section-header');
    const stickyHeader = document.querySelector('sticky-header');
    if (!header?.classList.contains('shopify-section-header-hidden') || typeof stickyHeader?.reveal !== 'function') {
      return false;
    }

    stickyHeader.reveal();
    return true;
  }

  function flyToCart(image, target) {
    const from = image.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    if (!from.width || !to.width || from.bottom < 0 || from.top > window.innerHeight) return Promise.resolve();

    const clone = document.createElement('img');
    clone.src = image.currentSrc || image.src;
    clone.alt = '';
    clone.className = 'quick-add-fly';
    Object.assign(clone.style, {
      top: `${from.top}px`,
      left: `${from.left}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
    });
    document.body.appendChild(clone);

    const x = to.left + to.width / 2 - (from.left + from.width / 2);
    const y = to.top + to.height / 2 - (from.top + from.height / 2);
    const flight = clone.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: 'translate(0, 0) scale(0.6)', opacity: 1, offset: 0.25 },
        { transform: `translate(${x}px, ${y}px) scale(${28 / from.width})`, opacity: 0.5 },
      ],
      { duration: 750, easing: 'ease-in-out' },
    );

    // The timeout covers background tabs, where animations may never finish.
    return Promise.race([flight.finished, wait(900)])
      .catch(() => {})
      .finally(() => clone.remove());
  }

  function updateCartBubble(headerHtml) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    const fresh =
      headerHtml && new DOMParser().parseFromString(headerHtml, 'text/html').getElementById('cart-icon-bubble');
    if (fresh) bubble.innerHTML = fresh.innerHTML;

    bubble.classList.remove('quick-add-bump');
    void bubble.offsetWidth;
    bubble.classList.add('quick-add-bump');
  }

  // Same "Added to Cart" event the product page sends (snippets/custom-klavio-add-to-cart-event.liquid),
  // plus a DOM event Convert goals can listen to.
  function trackAdd(item, source) {
    if (!item) return;
    document.dispatchEvent(new CustomEvent('quick-add:added', { detail: { item, source } }));

    const klaviyo = window.klaviyo || window._learnq;
    if (!klaviyo?.push) return;

    fetch(`${window.routes?.cart_url || '/cart'}.js`)
      .then((response) => response.json())
      .then((cart) => {
        klaviyo.push([
          'track',
          'Added to Cart',
          {
            $value: cart.total_price / 100,
            total_price: cart.total_price / 100,
            original_total_price: cart.original_total_price / 100,
            total_discount: cart.total_discount,
            items: cart.items,
            AddedItemProductName: item.product_title,
            AddedItemProductID: item.product_id,
            AddedItemVariantID: item.variant_id,
            AddedItemSKU: item.sku,
            AddedItemPrice: item.final_price / 100,
            AddedItemQuantity: item.quantity,
            AddedItemImageURL: item.image,
            AddedItemURL: window.location.origin + item.url,
            Source: source,
          },
        ]);
      })
      .catch(() => {});
  }

  function setLoading(button, loading) {
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', String(loading));
  }

  function showAdded(button) {
    const label = button.querySelector('.card-quick-add__label');
    clearTimeout(button.quickAddTimer);
    button.dataset.label = button.dataset.label || label.textContent;
    label.textContent = 'Added';
    button.classList.add('is-added');
    button.quickAddTimer = setTimeout(() => resetButton(button), ADDED_DURATION);
  }

  function resetButton(button) {
    clearTimeout(button.quickAddTimer);
    button.classList.remove('is-added', 'is-loading');
    button.removeAttribute('aria-busy');
    if (button.dataset.label) button.querySelector('.card-quick-add__label').textContent = button.dataset.label;
  }

  function showError(node, message) {
    if (!node) return;
    clearTimeout(node.quickAddTimer);
    node.textContent = message;
    node.hidden = false;
    node.quickAddTimer = setTimeout(() => (node.hidden = true), ERROR_DURATION);
  }

  function hideError(node) {
    if (!node) return;
    clearTimeout(node.quickAddTimer);
    node.hidden = true;
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Variant picker

  function openPicker(trigger, card) {
    let product;
    try {
      product = JSON.parse(card.querySelector('[data-quick-add-product]').textContent);
    } catch (error) {
      window.location.href = card.querySelector('a[href]').href;
      return;
    }

    modal = modal || createModal();
    modal.trigger = trigger;
    modal.card = card;
    renderPicker(product);

    modal.root.hidden = false;
    document.documentElement.classList.add('quick-add-modal-open');
    void modal.root.offsetWidth;
    modal.root.classList.add('is-open');
    (modal.chips.querySelector('[aria-pressed="true"]') || modal.close).focus({ preventScroll: true });
  }

  function closePicker() {
    if (!modal?.root.classList.contains('is-open')) return;

    modal.root.classList.remove('is-open');
    document.documentElement.classList.remove('quick-add-modal-open');
    setTimeout(() => {
      if (!modal.root.classList.contains('is-open')) modal.root.hidden = true;
    }, 250);
    modal.trigger?.focus({ preventScroll: true });
  }

  function createModal() {
    const root = document.createElement('div');
    root.className = 'quick-add-modal';
    root.hidden = true;
    root.innerHTML = `
      <div class="quick-add-modal__overlay" data-quick-add-close></div>
      <div class="quick-add-modal__panel" role="dialog" aria-modal="true" aria-labelledby="QuickAddTitle">
        <button type="button" class="quick-add-modal__close" aria-label="Close" data-quick-add-close>
          <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="quick-add-modal__head">
          <img class="quick-add-modal__image" alt="" width="88" height="88">
          <div>
            <h2 class="quick-add-modal__title" id="QuickAddTitle"></h2>
            <div class="quick-add-modal__price"></div>
          </div>
        </div>
        <fieldset class="quick-add-modal__options">
          <legend class="quick-add-modal__legend"></legend>
          <div class="quick-add-modal__chips"></div>
        </fieldset>
        <button type="button" class="card-quick-add__button quick-add-button quick-add-modal__submit">
          ${BUTTON_ICONS}
          <span class="card-quick-add__label">Add to cart</span>
        </button>
        <p class="card-quick-add__error" role="alert" hidden></p>
        <a class="quick-add-modal__details" href="#">View full details</a>
      </div>`;
    document.body.appendChild(root);

    const panel = root.querySelector('.quick-add-modal__panel');
    const parts = {
      root,
      panel,
      close: root.querySelector('.quick-add-modal__close'),
      image: root.querySelector('.quick-add-modal__image'),
      title: root.querySelector('.quick-add-modal__title'),
      price: root.querySelector('.quick-add-modal__price'),
      legend: root.querySelector('.quick-add-modal__legend'),
      chips: root.querySelector('.quick-add-modal__chips'),
      submit: root.querySelector('.quick-add-modal__submit'),
      error: root.querySelector('.card-quick-add__error'),
      details: root.querySelector('.quick-add-modal__details'),
    };

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-quick-add-close]')) {
        closePicker();
        return;
      }
      const chip = event.target.closest('.quick-add-modal__chip');
      if (chip && !chip.disabled) selectVariant(Number(chip.dataset.index));
    });
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePicker();
      if (event.key === 'Tab') trapFocus(event, panel);
    });
    parts.submit.addEventListener('click', addFromPicker);

    return parts;
  }

  function renderPicker(product) {
    modal.product = product;
    modal.title.textContent = product.title;
    modal.legend.textContent = product.option;
    modal.details.href = product.url;
    modal.chips.replaceChildren(
      ...product.variants.map((variant, index) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'quick-add-modal__chip';
        chip.dataset.index = index;
        chip.textContent = variant.title;
        chip.disabled = !variant.available;
        if (!variant.available) chip.setAttribute('aria-label', `${variant.title}, sold out`);
        return chip;
      }),
    );

    hideError(modal.error);
    resetButton(modal.submit);
    const firstAvailable = product.variants.findIndex((variant) => variant.available);
    selectVariant(Math.max(firstAvailable, 0));
  }

  function selectVariant(index) {
    const variant = modal.product.variants[index];
    modal.variant = variant;
    modal.chips.querySelectorAll('.quick-add-modal__chip').forEach((chip) => {
      chip.setAttribute('aria-pressed', String(Number(chip.dataset.index) === index));
    });

    const imageSrc = variant.image || modal.product.image;
    modal.image.hidden = !imageSrc;
    if (imageSrc) modal.image.src = imageSrc;

    const current = document.createElement('span');
    current.className = 'quick-add-modal__current';
    current.textContent = variant.price;
    const priceParts = [current];
    if (variant.compare) {
      const struck = document.createElement('s');
      struck.textContent = variant.compare;
      priceParts.unshift(struck);
    }
    if (modal.product.member) {
      const note = document.createElement('span');
      note.className = 'quick-add-modal__member';
      note.textContent = 'Loyal Quilter price';
      priceParts.push(note);
    }
    modal.price.replaceChildren(...priceParts);
    modal.price.classList.toggle('is-sale', Boolean(variant.compare));

    modal.submit.disabled = !variant.available;
  }

  async function addFromPicker() {
    const { submit, variant, card } = modal;
    if (!variant || submit.disabled || submit.classList.contains('is-loading')) return;

    hideError(modal.error);
    setLoading(submit, true);

    try {
      const data = await requestAdd(variant.id);
      setLoading(submit, false);
      showAdded(submit);
      trackAdd(data.items?.[0], 'Collection quick add');
      setTimeout(() => {
        closePicker();
        celebrate(card, data.sections?.['custom-header']);
      }, 600);
    } catch (err) {
      setLoading(submit, false);
      showError(modal.error, err.message);
    }
  }

  function trapFocus(event, panel) {
    const focusable = [...panel.querySelectorAll('button:not([disabled]), a[href]')];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
})();
